import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cableResponse } from "../../model/calculations/calculation";
import { inflowOutput } from "../../model/calculations/fluid";
import { wellInput } from "../../model/jetlift/jetlift";

interface WellState {
    loading: boolean;
    error: string | undefined;
    wellInput: wellInput | null;
    inflowOutput: inflowOutput | null;
    cableDetails: cableResponse | null;
}

export const initialState: WellState = {
    wellInput: null,
    loading: false,
    error: undefined,
    inflowOutput: null,
    cableDetails: null,
};

const wellSlice = createSlice({
    name: "jetlift/well",
    initialState,
    reducers: {
        resetWellDetails: () => ({
            ...initialState
        }),
        setWellTabDetails: (state, action) => {
            const payload = action?.payload;
            if (payload) {
                state.wellInput = {
                    ...payload,
                    inflowType: payload.inflowType ? Object?.keys(payload?.inflowType)?.map((key) => ({
                        value: payload?.inflowType[key],
                        label: key,
                    })) : [],
                };
            }
        },
        setTubingPressure: (state: any, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    tubingPressure: action?.payload
                };
            }
        },
        setCasingPressure: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    casingPressure: action?.payload
                };
            }
        },
        setInflowType: (state, action) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    inflowType: action?.payload && Object?.keys(action?.payload)?.length > 0 ? Object?.keys(action?.payload)?.map((key) => ({
                        value: action?.payload[key],
                        label: key,
                    })) : [],
                };
            }
        },
        setInflowTypeLstVal: (state: any, action: PayloadAction<string>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    inflowTypeLstVal: action?.payload
                };
            }
        },
        setProductivityIndex: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    piVal: action?.payload
                };
            }
        },
        setStaticBottomHolePressurePI: (state, action: PayloadAction<number | null>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    reservoirPressure: action?.payload
                };
            }
        },
        setStaticFluidLevelFromSurfacePI: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    staticFluidLvl: action?.payload
                };
            }
        },
        setStaticFluidLevelFromSurfacePI2: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    staticFluidLvl: action?.payload
                };
            }
        },
        setDesiredProductionRate: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    productionRate: action?.payload
                };
            }
        },
        setStaticBottomHolePressureP: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    sbhpForPressre: action?.payload
                };
            }
        },
        setFlowingBottomHolePressureP: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    intakePressure: action?.payload
                };
            }
        },
        setTestFlowRate: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    flowRate: action?.payload
                };
            }
        },
        setStaticFluidLevelFromSurfaceHead: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    staticFluidLvlForHd: action?.payload
                };
            }
        },
        setProducingFluidLevelFromSurfaceHead: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    prdcngFluidLvl: action?.payload
                };
            }
        },
        setTestFlowRateHead: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    flowRateForHd: action?.payload
                };
            }
        },
        setSettingDepthReference: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    setDepthType: action?.payload
                };
            }
        },
        setSettingDepth: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    pumpSetting: action?.payload
                };
            }
        },
        setPumpSettingVerticalDepth: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    tvd: action?.payload
                };
            }
        },
        setReservoirDatum: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    perforation: action?.payload
                };
            }
        },
        setFlopValue: (state, action: PayloadAction<number | null>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    flop: action?.payload
                };
            }
        },
        setHiddenHeadPIIPType: (state, action: PayloadAction<string>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    hiddenPresHeadPIIPType: action?.payload
                };
            }
        },
        setPumpSuctionPressure: (state, action: PayloadAction<number>) => {
            if (state?.wellInput) {
                state.wellInput = {
                    ...state.wellInput,
                    desiredPumpSuctionPressure: action?.payload
                };
            }
        },
        setInflowCalculations: (state, action: PayloadAction<any>) => {
            state.inflowOutput = action.payload;
        },
    },
});

export const {
    resetWellDetails,
    setWellTabDetails,
    setTubingPressure,
    setCasingPressure,
    setInflowType,
    setInflowTypeLstVal,
    setProductivityIndex,
    setStaticBottomHolePressurePI,
    setStaticFluidLevelFromSurfacePI,
    setStaticFluidLevelFromSurfacePI2,
    setDesiredProductionRate,
    setStaticBottomHolePressureP,
    setFlowingBottomHolePressureP,
    setTestFlowRate,
    setStaticFluidLevelFromSurfaceHead,
    setProducingFluidLevelFromSurfaceHead,
    setTestFlowRateHead,
    setSettingDepthReference,
    setSettingDepth,
    setPumpSettingVerticalDepth,
    setReservoirDatum,
    setFlopValue,
    setHiddenHeadPIIPType,
    setPumpSuctionPressure,
    setInflowCalculations,
} = wellSlice.actions;

export default wellSlice.reducer;
